import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, Loader2, User, Users, UserCheck, 
  MessageSquare, Calendar, Globe, ExternalLink, FileText, Download, Eye, TrendingUp, BarChart3,
  Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, Cell } from 'recharts';
import { TwitterNetworkGraph } from '@/components/TwitterNetworkGraph';
import { XTweetLinkAnalysisGraph } from '@/components/XTweetLinkAnalysisGraph';
import { getMediaUrlsFromPost, PostMediaGrid } from '@/components/PostMedia';
import XEventInvestigation from './XEventInvestigation';

interface UserInfo {
  type?: string;
  userName?: string;
  username?: string;
  screenName?: string;
  url?: string;
  website?: string;
  id?: string;
  userId?: string;
  name?: string;
  displayName?: string;
  isBlueVerified?: boolean;
  verified?: boolean;
  verifiedType?: string;
  profilePicture?: string;
  avatar?: string;
  profile_image_url?: string;
  coverPicture?: string;
  description?: string;
  bio?: string;
  location?: string;
  geoLocation?: string;
  followers?: number;
  followersCount?: number;
  following?: number;
  followingCount?: number;
  canDm?: boolean;
  createdAt?: string;
  created_at?: string;
  joinDate?: string;
  favouritesCount?: number;
  statusesCount?: number;
  tweetsCount?: number; // Same as statusesCount, provided by backend
  mediaCount?: number;
}

interface InvestigationResult {
  username: string;
  timestamp: string;
  user_info: {
    success: boolean;
    data?: UserInfo;
    rate_limited?: boolean;
    source?: string; // 'primary' or 'tweepy'
  };
  followers?: {
    success: boolean;
    followers?: UserInfo[];
    has_next_page?: boolean;
    next_cursor?: string;
    rate_limited?: boolean;
    error?: string;
    status_code?: number;
    source?: string; // 'primary' or 'tweepy'
  };
  followings?: {
    success: boolean;
    followings?: UserInfo[];
    has_next_page?: boolean;
    next_cursor?: string;
    rate_limited?: boolean;
    error?: string;
    status_code?: number;
    source?: string; // 'primary' or 'tweepy'
  };
  tweets?: {
    success: boolean;
    tweets?: any[];
    rate_limited?: boolean;
    error?: string;
    status_code?: number;
    source?: string; // 'primary' or 'tweepy'
  };
  summary: {
    user_id?: string;
    name?: string;
    verified?: boolean;
    followers?: number;
    following?: number;
    statuses_count?: number;
    created_at?: string;
    followers_count?: number;
    followings_count?: number;
  };
  grok_enhanced?: {
    profile_data?: string;
    citations?: string[];
  };
  grok_tweet_analysis?: {
    success: boolean;
    analysis?: string;
    citations?: string[];
    query?: string;
  };
  grok_sentiment_analysis?: {
    success: boolean;
    analysis?: string;
    sentiment_data?: {
      overall?: {
        positive?: number;
        negative?: number;
        neutral?: number;
      };
      post_sentiments?: Array<{
        post_id: string;
        sentiment: string;
        text: string;
      }>;
      themes?: string[];
    };
    citations?: string[];
  };
  bot_detection?: {
    success: boolean;
    suspicious_count?: number;
    total_sampled?: number;
    suspicious_percentage?: number;
    risk_level?: string;
    suspicious_accounts?: Array<{
      username: string;
      followers: number;
      following: number;
      tweets: number;
      bot_score: number;
      reasons: string[];
      link?: string;
    }>;
    message?: string;
  };
  grok_relationship_analysis?: {
    success: boolean;
    analysis?: string;
    insights?: {
      mutual_connections?: string[];
      influencer_relationships?: string[];
      community_clusters?: string[];
      engagement_patterns?: Record<string, any>;
      network_influence_score?: number;
    };
    citations?: string[];
  };
  search_results?: {
    query?: string;
    posts?: any[];
    total?: number;
    error?: string;
    sentiment_analysis?: {
      overall?: {
        positive?: number;
        negative?: number;
        neutral?: number;
      };
      themes?: string[];
      post_sentiments?: any[];
      trends?: any;
    };
  };
  success: boolean;
  combined_from?: string[];
  combined_count?: number;
}

interface TweetAnalytics {
  topWords: { word: string; count: number }[];
  hourlyDistribution: { hour: number; count: number }[];
  topHashtags: { tag: string; count: number }[];
  topMentions: { handle: string; count: number }[];
}

const STOPWORDS = new Set([
  'about','after','again','against','all','also','and','any','as','at','back','be','because','been',
  'but','by','can','coming','could','down','during','each','even','for','from','further','get','got',
  'had','has','have','he','her','here','hers','him','his','how','if','in','into','is','it','its','just',
  'like','more','most','no','not','now','of','on','one','only','or','other','our','out','over','own',
  'said','see','she','should','so','some','such','than','that','the','their','them','then','there','these',
  'they','this','those','through','to','too','us','very','was','we','well','were','what','when','which',
  'who','will','with','would','you','your'
]);

const extractTweetId = (tweet: any, fallbackIndex: number) => {
  const rawId = tweet?.id_str ?? tweet?.id ?? tweet?.tweetId ?? tweet?.tweet_id ?? tweet?.clientTweetId;
  return rawId ? String(rawId) : `tweet-${fallbackIndex}`;
};

const extractTweetText = (tweet: any) => {
  return (
    tweet?.text ||
    tweet?.fullText ||
    tweet?.content ||
    tweet?.description ||
    tweet?.message ||
    ''
  );
};

const extractTweetTimestamp = (tweet: any) => {
  return (
    tweet?.createdAt ||
    tweet?.created_at ||
    tweet?.date ||
    tweet?.timestamp ||
    tweet?.time
  );
};

const normalizeHashtags = (raw: any) => {
  if (!raw) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (!entry) return '';
        if (typeof entry === 'string') return entry.replace(/^#/, '');
        return entry?.tag || entry?.text || entry?.hashtag || '';
      })
      .map((value) => value.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeMentions = (raw: any) => {
  if (!raw) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (!entry) return '';
        if (typeof entry === 'string') return entry.replace(/^@/, '');
        return entry?.username || entry?.screen_name || entry?.name || '';
      })
      .map((value) => value.trim())
      .filter(Boolean);
  }
  return [];
};

const buildTweetAnalytics = (tweets: any[]): TweetAnalytics => {
  const wordCounts = new Map<string, number>();
  const hourly: Record<number, number> = {};
  const hashtagCounts = new Map<string, number>();
  const mentionCounts = new Map<string, number>();

  tweets.forEach((tweet) => {
    const text = (extractTweetText(tweet) || '').toLowerCase();
    const words = text.split(/\W+/).filter((word: string) => word.length > 3 && !STOPWORDS.has(word));
    words.forEach((word: string) => {
      wordCounts.set(word, (wordCounts.get(word) ?? 0) + 1);
    });

    const createdAt = new Date(extractTweetTimestamp(tweet) || '');
    if (!Number.isNaN(createdAt.getTime())) {
      const hour = createdAt.getHours();
      hourly[hour] = (hourly[hour] ?? 0) + 1;
    }

    const hashtags =
      normalizeHashtags(tweet.hashtags) ||
      normalizeHashtags(tweet.entities?.hashtags) ||
      [];
    hashtags.forEach((tag: string) => {
      hashtagCounts.set(tag.toLowerCase(), (hashtagCounts.get(tag.toLowerCase()) ?? 0) + 1);
    });

    const mentions =
      normalizeMentions(tweet.mentions) ||
      normalizeMentions(tweet.entities?.user_mentions) ||
      [];
    mentions.forEach((mention: string) => {
      mentionCounts.set(mention.toLowerCase(), (mentionCounts.get(mention.toLowerCase()) ?? 0) + 1);
    });
  });

  const sortedWords = Array.from(wordCounts.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([word, count]) => ({ word, count }));

  const sortedHashtags = Array.from(hashtagCounts.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([tag, count]) => ({ tag, count }));

  const sortedMentions = Array.from(mentionCounts.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([handle, count]) => ({ handle, count }));

  const hourlyDistribution = Object.keys(hourly)
    .map((hour) => ({ hour: Number(hour), count: hourly[Number(hour)] }))
    .sort((a, b) => a.hour - b.hour);

  return {
    topWords: sortedWords,
    hourlyDistribution,
    topHashtags: sortedHashtags,
    topMentions: sortedMentions
  };
};

const GRAPH_LEGEND_ENTRIES = [
  { color: '#00ff00', label: 'Investigated account' },
  { color: '#ffaa00', label: 'Mutual connections' },
  { color: '#00aaff', label: 'Followers' },
  { color: '#aa00ff', label: 'Following' }
];

const GRAPH_SORT_OPTIONS = [
  { label: 'Default', value: 'default' },
  { label: 'Followers count', value: 'followers' },
  { label: 'Following count', value: 'following' }
];

const GRAPH_METRIC_FIELDS: Record<string, string[]> = {
  followers: ['followers', 'followersCount', 'followers_count', 'public_metrics.followers_count'],
  following: ['following', 'followingCount', 'following_count', 'public_metrics.following_count']
};

const getGraphMetricValue = (entity: any, metric: 'followers' | 'following') => {
  const fields = GRAPH_METRIC_FIELDS[metric];
  for (const field of fields) {
    const segments = field.split('.');
    let current = entity;
    for (const segment of segments) {
      if (current == null) break;
      current = current[segment];
    }
    if (current !== undefined && current !== null) {
      const parsed = typeof current === 'string' ? parseInt(current, 10) : Number(current);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
};

const sortGraphNodes = (nodes: any[], metric: 'followers' | 'following', sortBy: string) => {
  if (sortBy === 'default') return nodes;
  return [...nodes].sort(
    (a, b) => getGraphMetricValue(b, metric) - getGraphMetricValue(a, metric)
  );
};

export default function XInvestigation() {
  const [investigationMode, setInvestigationMode] = useState<'user' | 'event'>('user');
  const [username, setUsername] = useState('');
  const [isInvestigating, setIsInvestigating] = useState(false);
  const [investigationResult, setInvestigationResult] = useState<InvestigationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTweetIds, setSelectedTweetIds] = useState<string[]>([]);
  const [aiFocus, setAiFocus] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiTimestamp, setAiTimestamp] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [reportLoadingId, setReportLoadingId] = useState<string | null>(null);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);
  const [selectedHistoryIds, setSelectedHistoryIds] = useState<Set<string>>(new Set());
  const [isCombining, setIsCombining] = useState(false);
  const [showFollowersInGraph, setShowFollowersInGraph] = useState(true);
  const [showFollowingsInGraph, setShowFollowingsInGraph] = useState(true);
  const [graphSortBy, setGraphSortBy] = useState<'default' | 'followers' | 'following'>('default');
  const [selectedGraphNode, setSelectedGraphNode] = useState<any>(null);
  
  const [queryType, setQueryType] = useState<'user_info' | 'topic_search' | 'field_search' | 'location_search' | null>('user_info');
  const [fetchingProfile, setFetchingProfile] = useState<string | null>(null);
  const [recentOpen, setRecentOpen] = useState(false);

  const handleInvestigation = async () => {
    if (!username.trim()) {
      setError('Please enter a Twitter/X username');
      return;
    }

    setIsInvestigating(true);
    setError(null);
    setInvestigationResult(null);
    setCurrentHistoryId(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const response = await fetch('/api/twitter/investigate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: username.trim().replace('@', ''), // Remove @ if present
          include_followers: true,
          include_followings: true,
          include_tweets: true,
          max_followers: 200,
          max_followings: 200,
          max_tweets: 20,
          force_refresh: false // Set to true to bypass cache
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.detail || errorData.error || `Investigation failed: ${response.statusText}`;
        
        // Provide user-friendly messages for specific error codes
        if (response.status === 402) {
          errorMessage = "Twitter API account requires payment or has insufficient credits. Please check your API account balance and add credits to continue.";
        } else if (response.status === 429) {
          errorMessage = "Rate limit exceeded. Please try again later.";
        } else if (response.status === 502) {
          errorMessage = errorMessage || "Twitter API service is temporarily unavailable. Please try again later.";
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setInvestigationResult(result);
      setCurrentHistoryId(result.history_id || null);
      setQueryType('user_info');
      fetchHistory();
      
      // Check for rate limit warnings
      if (result.followers?.rate_limited || result.followings?.rate_limited) {
        setError('Rate limit reached. Some data may be incomplete. Please try again later.');
      }
    } catch (err) {
      console.error('Investigation error:', err);
      setError(err instanceof Error ? err.message : 'Investigation failed');
    } finally {
      setIsInvestigating(false);
    }
  };

  const fetchHistory = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setHistoryEntries([]);
      return;
    }

    setHistoryLoading(true);
    setHistoryError(null);

    try {
      const response = await fetch('/api/twitter/investigation/history', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load investigation history');
      }

      const data = await response.json();
      setHistoryEntries(data.history || []);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Unable to load history');
      setHistoryEntries([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    if (investigationResult && !isInvestigating) {
      document.getElementById('investigation-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [investigationResult, isInvestigating]);

  const loadHistoryInvestigation = async (entry: any) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Authentication token is missing.');
      return;
    }

    setError(null);

    try {
      const response = await fetch(`/api/twitter/investigation/cached/${entry.history_id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed to load cached investigation');
      }

      const historyResult = await response.json();
      setInvestigationResult(historyResult);
      setCurrentHistoryId(entry.history_id || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load cached investigation');
    }
  };

  const rerunInvestigation = async (entry: any) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Authentication token is missing.');
      return;
    }

    setError(null);
    setIsInvestigating(true);
    setUsername(entry.username);

    try {
      const response = await fetch('/api/twitter/investigate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: entry.username.trim().replace('@', ''),
          include_followers: true,
          include_followings: true,
          include_tweets: true,
          max_followers: 200,
          max_followings: 200,
          max_tweets: 20,
          force_refresh: true // Force refresh to get new data
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.detail || errorData.error || `Investigation failed: ${response.statusText}`;
        
        if (response.status === 402) {
          errorMessage = "Twitter API account requires payment or has insufficient credits. Please check your API account balance and add credits to continue.";
        } else if (response.status === 429) {
          errorMessage = "Rate limit exceeded. Please try again later.";
        } else if (response.status === 502) {
          errorMessage = errorMessage || "Twitter API service is temporarily unavailable. Please try again later.";
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setInvestigationResult(result);
      fetchHistory();
    } catch (err) {
      console.error('Rerun investigation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to rerun investigation');
    } finally {
      setIsInvestigating(false);
    }
  };

  const toggleHistorySelection = (historyId: string) => {
    setSelectedHistoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(historyId)) {
        newSet.delete(historyId);
      } else {
        newSet.add(historyId);
      }
      return newSet;
    });
  };

  const combineInvestigations = async () => {
    if (selectedHistoryIds.size < 2) {
      setError('Please select at least 2 investigations to combine');
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Authentication token is missing.');
      return;
    }

    setIsCombining(true);
    setError(null);

    try {
      const response = await fetch('/api/twitter/investigation/combine', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          history_ids: Array.from(selectedHistoryIds)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to combine investigations');
      }

      const combinedResult = await response.json();
      setInvestigationResult(combinedResult);
      setSelectedHistoryIds(new Set()); // Clear selection
      fetchHistory();
    } catch (err) {
      console.error('Combine investigations error:', err);
      setError(err instanceof Error ? err.message : 'Failed to combine investigations');
    } finally {
      setIsCombining(false);
    }
  };

  const generateHistoryReport = async (entry: any) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setAiError('Authentication token is missing.');
      return;
    }

    setIsAiAnalyzing(true);
    setReportLoadingId(entry.history_id);
    setAiError(null);
    setAiAnalysis(null);

    try {
      // Check if current investigation result is combined
      const isCombined = investigationResult?.combined_from && investigationResult.combined_from.length > 0;
      
      if (isCombined && investigationResult) {
        // For combined investigations, generate report from current result
        const response = await fetch('/api/twitter/investigation/report/combined', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            investigation_result: investigationResult,
            focus: aiFocus?.trim() || undefined
          })
        });

        const aiResult = await response.json();
        if (!response.ok) {
          throw new Error(aiResult.detail || 'AI report generation failed');
        }

        setAiAnalysis(aiResult.analysis);
        setAiTimestamp(aiResult.timestamp || new Date().toISOString());
      } else {
        // For single investigations, use history_id
        const response = await fetch('/api/twitter/investigation/report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            history_id: entry.history_id,
            focus: aiFocus?.trim() || undefined
          })
        });

        const aiResult = await response.json();
        if (!response.ok) {
          throw new Error(aiResult.detail || 'AI report generation failed');
        }

        setAiAnalysis(aiResult.analysis);
        setAiTimestamp(aiResult.timestamp || new Date().toISOString());
      }
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI report generation failed');
    } finally {
      setIsAiAnalyzing(false);
      setReportLoadingId(null);
    }
  };

  const generatePDFReport = async (entry: any) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setAiError('Authentication token is missing.');
      return;
    }

    setIsAiAnalyzing(true);
    setReportLoadingId(entry.history_id);
    setAiError(null);

    try {
      const response = await fetch('/api/twitter/investigation/report/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          history_id: entry.history_id,
          focus: aiFocus?.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'PDF report generation failed');
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `osint_report_${entry.username}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'PDF report generation failed');
    } finally {
      setIsAiAnalyzing(false);
      setReportLoadingId(null);
    }
  };

  useEffect(() => {
    setSelectedTweetIds([]);
    setAiAnalysis(null);
    setAiError(null);
    setAiTimestamp(null);
    setAiFocus('');
    setShowFollowersInGraph(true);
    setShowFollowingsInGraph(true);
    setGraphSortBy('default');
    setSelectedGraphNode(null);
  }, [investigationResult?.timestamp]);

  const followerList = investigationResult?.followers?.followers || [];
  const followingList = investigationResult?.followings?.followings || [];
  const tweets = investigationResult?.tweets?.tweets || [];
  const tweetAnalytics = useMemo(() => buildTweetAnalytics(tweets), [tweets]);

  const followerNodesSource = investigationResult?.followers?.followers || [];
  const followingNodesSource = investigationResult?.followings?.followings || [];

  const filteredGraphFollowers = useMemo(() => {
    const base = showFollowersInGraph ? followerNodesSource : [];
    if (graphSortBy === 'followers') {
      return sortGraphNodes(base, 'followers', graphSortBy);
    }
    if (graphSortBy === 'following') {
      return sortGraphNodes(base, 'following', graphSortBy);
    }
    return base;
  }, [followerNodesSource, showFollowersInGraph, graphSortBy]);

  const filteredGraphFollowings = useMemo(() => {
    const base = showFollowingsInGraph ? followingNodesSource : [];
    if (graphSortBy === 'followers') {
      return sortGraphNodes(base, 'followers', graphSortBy);
    }
    if (graphSortBy === 'following') {
      return sortGraphNodes(base, 'following', graphSortBy);
    }
    return base;
  }, [followingNodesSource, showFollowingsInGraph, graphSortBy]);

  const toggleTweetSelection = (tweetId: string) => {
    setSelectedTweetIds((prev) => {
      if (prev.includes(tweetId)) {
        return prev.filter((id) => id !== tweetId);
      }
      return [...prev, tweetId];
    });
  };

  const handleSelectAllTweets = () => {
    const allTweetIds = tweets.map((tweet, index) => extractTweetId(tweet, index));
    setSelectedTweetIds(allTweetIds);
  };

  const handleClearSelection = () => {
    setSelectedTweetIds([]);
  };

  const buildTweetPayload = (tweet: any, index: number) => {
    const hashtags = [
      ...normalizeHashtags(tweet.hashtags),
      ...normalizeHashtags(tweet.entities?.hashtags)
    ];
    const mentions = [
      ...normalizeMentions(tweet.mentions),
      ...normalizeMentions(tweet.entities?.user_mentions)
    ];

    return {
      id: extractTweetId(tweet, index),
      text: extractTweetText(tweet),
      createdAt: extractTweetTimestamp(tweet),
      hashtags,
      mentions,
      url: tweet.url || tweet.permalink || tweet.share_url,
      likeCount: tweet.likeCount ?? tweet.likes,
      retweetCount: tweet.retweetCount ?? tweet.retweets,
      replyCount: tweet.replyCount ?? tweet.replies
    };
  };

  const handleSendTweetsToAI = async () => {
    if (!investigationResult || !tweets.length || !selectedTweetIds.length) {
      setAiError('Please select at least one post to analyze');
      return;
    }

    setIsAiAnalyzing(true);
    setAiError(null);
    setAiAnalysis(null);
    setAiTimestamp(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Authentication token is missing. Please login again.');
      }

      // Get selected tweets
      const selectedTweets = tweets.filter((tweet, index) => {
        const tweetId = extractTweetId(tweet, index);
        return selectedTweetIds.includes(tweetId);
      });

      const payloadTweets = selectedTweets.map((tweet, index) => buildTweetPayload(tweet, index));
      if (!payloadTweets.length) {
        throw new Error('No tweets available for AI analysis');
      }

      const userSummary = investigationResult.user_info?.data || {};
      const customQuestion = aiFocus.trim();

      const response = await fetch('/api/twitter/investigate/ai-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          username: investigationResult.username || 'selected_posts',
          tweets: payloadTweets,
          selected_tweet_ids: selectedTweetIds,
          focus: customQuestion || undefined,
          user_summary: {
            name: userSummary.name || userSummary.displayName || investigationResult.summary?.name || 'Selected Posts',
            description: userSummary.description || userSummary.bio || (customQuestion ? `Analysis request: ${customQuestion}` : ''),
            location: userSummary.location || userSummary.geoLocation,
            verified: userSummary.isBlueVerified || userSummary.verified || false
          }
        })
      });

      const aiResult = await response.json();
      if (!response.ok) {
        throw new Error(aiResult.detail || aiResult.message || 'AI analysis failed');
      }

      setAiAnalysis(aiResult.analysis);
      setAiTimestamp(aiResult.timestamp || new Date().toISOString());
    } catch (err) {
      console.error('AI analysis failed', err);
      setAiError(err instanceof Error ? err.message : 'AI analysis failed');
    } finally {
      setIsAiAnalyzing(false);
    }
  };

  // Graph data building removed - using TwitterNetworkGraph component instead
  /* const buildGraphData = () => {
    if (!investigationResult) return null;

    const nodes: any[] = [];
    const edges: any[] = [];
    const userInfo = investigationResult.user_info?.data;

    // Add main user node
    if (userInfo) {
      nodes.push({
        id: `user_${userInfo.id}`,
        type: 'account',
        label: userInfo.name || userInfo.userName,
        props: {
          username: userInfo.userName,
          verified: userInfo.isBlueVerified,
          followers: userInfo.followers,
          following: userInfo.following,
          description: userInfo.description,
          location: userInfo.location,
          profile_picture: userInfo.profilePicture
        },
        confidence: 1.0,
        created_at: new Date().toISOString()
      });
    }

    // Add followers
    if (investigationResult.followers?.success && investigationResult.followers.followers) {
      investigationResult.followers.followers.forEach((follower, index) => {
        const followerId = `follower_${follower.id || index}`;
        nodes.push({
          id: followerId,
          type: 'account',
          label: follower.name || follower.userName,
          props: {
            username: follower.userName,
            verified: follower.isBlueVerified,
            followers: follower.followers,
            following: follower.following,
            profile_picture: follower.profilePicture
          },
          confidence: 0.8,
          created_at: new Date().toISOString()
        });

        if (userInfo) {
          edges.push({
            id: `edge_${userInfo.id}_${follower.id || index}`,
            source: `user_${userInfo.id}`,
            target: followerId,
            relation: 'has_follower',
            weight: 0.8,
            confidence: 0.8,
            created_at: new Date().toISOString()
          });
        }
      });
    }

    // Add followings
    if (investigationResult.followings?.success && investigationResult.followings.followings) {
      investigationResult.followings.followings.forEach((following, index) => {
        const followingId = `following_${following.id || index}`;
        
        // Check if node already exists (might be in both followers and followings)
        const existingNode = nodes.find(n => n.id === followingId || 
          (n.props?.username === following.userName && n.type === 'account'));
        
        if (!existingNode) {
          nodes.push({
            id: followingId,
            type: 'account',
            label: following.name || following.userName,
            props: {
              username: following.userName,
              verified: following.isBlueVerified,
              followers: following.followers,
              following: following.following,
              profile_picture: following.profilePicture
            },
            confidence: 0.8,
            created_at: new Date().toISOString()
          });
        }

        if (userInfo) {
          edges.push({
            id: `edge_${userInfo.id}_following_${following.id || index}`,
            source: `user_${userInfo.id}`,
            target: existingNode?.id || followingId,
            relation: 'follows',
            weight: 0.8,
            confidence: 0.8,
            created_at: new Date().toISOString()
          });
        }
      });
    }

    return {
      nodes,
      edges,
      metadata: {
        total_nodes: nodes.length,
        total_edges: edges.length,
        query: username,
        search_timestamp: new Date().toISOString()
      },
      facets: {}
    };
  }; */

  // Graph data building removed - using TwitterNetworkGraph component instead

  return (
    <div className="space-y-6">
      {/* Header with Mode Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">X (Twitter) Investigation</h1>
          <p className="text-muted-foreground">
            Comprehensive investigation tool for X/Twitter accounts, networks, and events
          </p>
        </div>
        <div className="flex items-center rounded-xl border p-1" style={{ background: 'rgba(10,10,10,0.8)', borderColor: 'rgba(0,255,0,0.2)' }}>
          <button
            onClick={() => setInvestigationMode('user')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              investigationMode === 'user'
                ? 'bg-emerald-500/20 text-emerald-400 shadow-sm'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <User className="w-4 h-4" />
            User Mode
          </button>
          <button
            onClick={() => setInvestigationMode('event')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              investigationMode === 'event'
                ? 'bg-emerald-500/20 text-emerald-400 shadow-sm'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <Zap className="w-4 h-4" />
            Event Mode
          </button>
        </div>
      </div>

      {/* Event Investigation Mode */}
      {investigationMode === 'event' && <XEventInvestigation />}

      {/* User Investigation Mode */}
      {investigationMode === 'user' && <>

      {/* Search Bar - Traditional username search only */}
      <Card className="border-emerald-500/50 bg-gradient-to-br from-emerald-950/20 to-slate-950/20">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter Twitter/X username (without @)"
                className="h-11 text-base"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleInvestigation();
                  }
                }}
                disabled={isInvestigating}
              />
            </div>
            <Button
              onClick={handleInvestigation}
              disabled={isInvestigating || !username.trim()}
              className="h-11 px-6 bg-emerald-600 hover:bg-emerald-700"
            >
              {isInvestigating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Investigating...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Investigate
                </>
              )}
            </Button>
          </div>
          {error && (
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent investigations - collapsible */}
      <Card>
        <CardHeader 
          className="cursor-pointer select-none hover:bg-muted/50 rounded-t-lg transition-colors" 
          onClick={() => setRecentOpen(!recentOpen)}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Recent investigations</CardTitle>
              <CardDescription>
                Reuse cached investigations to limit API spend and generate reports from previous findings.
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">
              {recentOpen ? '▼ Collapse' : '▶ Expand'}
            </Badge>
          </div>
        </CardHeader>
        {recentOpen && (
        <CardContent className="space-y-3 border-t">
          {historyLoading ? (
            <p className="text-sm text-muted-foreground">Loading history...</p>
          ) : historyEntries.length ? (
            <div className="space-y-3">
              {selectedHistoryIds.size >= 2 && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-400">
                      {selectedHistoryIds.size} investigation{selectedHistoryIds.size > 1 ? 's' : ''} selected
                    </span>
                    <Button
                      size="sm"
                      onClick={combineInvestigations}
                      disabled={isCombining}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {isCombining ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          Combining...
                        </>
                      ) : (
                        <>
                          <BarChart3 className="mr-2 h-3 w-3" />
                          Combine & Generate Report
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              {historyEntries.map((entry: any) => {
                const summary = entry.summary || {};
                const metadata = entry.metadata || {};
                const isSelected = selectedHistoryIds.has(entry.history_id);
                return (
                  <div key={entry.history_id} className={`rounded-lg border p-3 sm:flex sm:items-center sm:justify-between transition-colors ${
                    isSelected ? 'border-green-500 bg-green-500/10' : 'border-gray-700 bg-gray-900/60'
                  }`}>
                    <div className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleHistorySelection(entry.history_id)}
                        className="mt-1 h-4 w-4 accent-green-400 cursor-pointer"
                      />
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                          <span>@{entry.username}</span>
                          <span className="text-xs text-muted-foreground">
                            · {new Date(entry.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span>
                            {summary.followers?.toLocaleString() ||
                              summary.followers_count?.toLocaleString() ||
                              metadata.followers_fetched ||
                              0} followers
                          </span>
                          <span>
                            {summary.following?.toLocaleString() ||
                              summary.followings_count?.toLocaleString() ||
                              metadata.followings_fetched ||
                              0} following
                          </span>
                          <span>
                            {summary.statuses_count?.toLocaleString() ||
                              metadata.tweets_fetched ||
                              0} tweets
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 sm:mt-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => loadHistoryInvestigation(entry)}
                        disabled={isInvestigating}
                      >
                        Load
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rerunInvestigation(entry)}
                        disabled={isInvestigating}
                        className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                      >
                        {isInvestigating && username === entry.username ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Rerunning...
                          </>
                        ) : (
                          <>
                            <TrendingUp className="mr-2 h-3 w-3" />
                            Rerun
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => generateHistoryReport(entry)}
                        disabled={isAiAnalyzing || reportLoadingId === entry.history_id}
                      >
                        {reportLoadingId === entry.history_id ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <FileText className="mr-2 h-3 w-3" />
                            Analysis
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => generatePDFReport(entry)}
                        disabled={isAiAnalyzing || reportLoadingId === entry.history_id}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {reportLoadingId === entry.history_id ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="mr-2 h-3 w-3" />
                            PDF
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : historyError ? (
            <p className="text-sm text-red-400">{historyError}</p>
          ) : (
            <p className="text-sm text-muted-foreground">No cached investigations yet.</p>
          )}
        </CardContent>
        )}
      </Card>

      {/* Results Display - shown prominently, scroll into view when ready */}
      {investigationResult && (
        <div className="space-y-6 scroll-mt-4" id="investigation-results">
          {/* Combined Investigation Notice */}
          {investigationResult.combined_from && investigationResult.combined_from.length > 0 && (
            <Card className="bg-green-500/10 border-green-500/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-green-400">
                      <BarChart3 className="h-5 w-5" />
                      <span className="font-semibold">
                        Combined Investigation ({investigationResult.combined_count || investigationResult.combined_from.length} sources)
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      This report combines data from {investigationResult.combined_count || investigationResult.combined_from.length} investigations
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      const token = localStorage.getItem('auth_token');
                      if (!token) {
                        setAiError('Authentication token is missing.');
                        return;
                      }

                      setIsAiAnalyzing(true);
                      setAiError(null);
                      setAiAnalysis(null);

                      try {
                        const response = await fetch('/api/twitter/investigation/report/combined', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            investigation_result: investigationResult,
                            focus: aiFocus?.trim() || undefined
                          })
                        });

                        const aiResult = await response.json();
                        if (!response.ok) {
                          throw new Error(aiResult.detail || 'AI report generation failed');
                        }

                        setAiAnalysis(aiResult.analysis);
                        setAiTimestamp(aiResult.timestamp || new Date().toISOString());
                      } catch (err) {
                        setAiError(err instanceof Error ? err.message : 'AI report generation failed');
                      } finally {
                        setIsAiAnalyzing(false);
                      }
                    }}
                    disabled={isAiAnalyzing}
                    className="ml-4"
                  >
                    {isAiAnalyzing ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-3 w-3" />
                        Generate Report
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generate Report & PDF - for single (non-combined) investigations */}
          {(!investigationResult.combined_from || !investigationResult.combined_from.length) && (() => {
            const historyId = currentHistoryId ?? historyEntries.find((e: any) => e.username === investigationResult?.username)?.history_id;
            if (!historyId) return null;
            return (
              <Card className="border-green-500/30 bg-green-500/5">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="font-semibold text-sm">Reports</p>
                        <p className="text-xs text-muted-foreground">Generate AI profile report or download PDF for this investigation.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                        onClick={() => generateHistoryReport({ history_id: historyId, username: investigationResult?.username })}
                        disabled={isAiAnalyzing || reportLoadingId === historyId}
                      >
                        {reportLoadingId === historyId ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FileText className="mr-2 h-3 w-3" />
                            Generate Report
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => generatePDFReport({ history_id: historyId, username: investigationResult?.username })}
                        disabled={isAiAnalyzing || reportLoadingId === historyId}
                      >
                        {reportLoadingId === historyId ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="mr-2 h-3 w-3" />
                            Download PDF
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">Account</span>
                </div>
                <div className="text-2xl font-bold">
                  {typeof investigationResult.summary.name === 'string' 
                    ? investigationResult.summary.name 
                    : (typeof investigationResult.username === 'string' 
                        ? investigationResult.username 
                        : 'Unknown User')}
                </div>
                {investigationResult.summary.verified && (
                  <Badge className="mt-1 bg-blue-500">Verified</Badge>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Followers</span>
                </div>
                <div className="text-2xl font-bold">
                  {investigationResult.summary.followers?.toLocaleString() || 
                   investigationResult.summary.followers_count?.toLocaleString() || '0'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {investigationResult.followers?.followers?.length || 0} fetched
                  {investigationResult.followers?.source === 'tweepy' && (
                    <span className="text-blue-500 ml-1">(via Tweepy)</span>
                  )}
                  {investigationResult.followers?.rate_limited && (
                    <span className="text-yellow-500 ml-1">(Rate limited)</span>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">Following</span>
                </div>
                <div className="text-2xl font-bold">
                  {investigationResult.summary.following?.toLocaleString() || 
                   investigationResult.summary.followings_count?.toLocaleString() || '0'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {investigationResult.followings?.followings?.length || 0} fetched
                  {investigationResult.followings?.source === 'tweepy' && (
                    <span className="text-blue-500 ml-1">(via Tweepy)</span>
                  )}
                  {investigationResult.followings?.rate_limited && (
                    <span className="text-yellow-500 ml-1">(Rate limited)</span>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-medium">Tweets</span>
                </div>
                <div className="text-2xl font-bold">
                  {(investigationResult.summary.statuses_count && investigationResult.summary.statuses_count > 0 
                    ? investigationResult.summary.statuses_count.toLocaleString()
                    : (investigationResult.tweets?.tweets?.length || 0).toLocaleString()) || '0'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {investigationResult.tweets?.tweets?.length || 0} fetched
                  {investigationResult.tweets?.rate_limited && (
                    <span className="text-yellow-500 ml-1">(Rate limited)</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Results Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={`grid w-full ${queryType === 'topic_search' || queryType === 'field_search' || queryType === 'location_search' ? 'grid-cols-3' : 'grid-cols-6'}`}>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              {queryType !== 'topic_search' && queryType !== 'field_search' && queryType !== 'location_search' && (
                <>
                  <TabsTrigger value="followers">Followers</TabsTrigger>
                  <TabsTrigger value="followings">Following</TabsTrigger>
                </>
              )}
              <TabsTrigger value="tweets">{queryType === 'topic_search' ? 'Posts' : 'Tweets'}</TabsTrigger>
              <TabsTrigger value="link-analysis">Link Analysis</TabsTrigger>
              {queryType !== 'topic_search' && queryType !== 'field_search' && queryType !== 'location_search' && (
                <TabsTrigger value="network">Network Graph</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>User Information</CardTitle>
                </CardHeader>
                <CardContent>
                  {investigationResult.user_info?.data && typeof investigationResult.user_info.data === 'object' && investigationResult.user_info.data !== null ? (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        {(investigationResult.user_info.data.profilePicture || 
                          investigationResult.user_info.data.avatar || 
                          investigationResult.user_info.data.profile_image_url) && (
                          <img 
                            src={investigationResult.user_info.data.profilePicture || 
                                 investigationResult.user_info.data.avatar || 
                                 investigationResult.user_info.data.profile_image_url} 
                            alt="Profile"
                            className="w-24 h-24 rounded-full object-cover border-2 border-green-500"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-bold">
                              {(typeof investigationResult.user_info.data.name === 'string' 
                                ? investigationResult.user_info.data.name 
                                : (typeof investigationResult.user_info.data.displayName === 'string'
                                    ? investigationResult.user_info.data.displayName
                                    : (typeof investigationResult.username === 'string'
                                        ? investigationResult.username
                                        : 'Unknown User')))}
                            </h3>
                            {(investigationResult.user_info.data.isBlueVerified || 
                              investigationResult.user_info.data.verified) && (
                              <Badge className="bg-blue-500">Verified</Badge>
                            )}
                          </div>
                          <p className="text-muted-foreground">
                            @{(typeof investigationResult.user_info.data.userName === 'string'
                              ? investigationResult.user_info.data.userName
                              : (typeof investigationResult.user_info.data.screenName === 'string'
                                  ? investigationResult.user_info.data.screenName
                                  : (typeof investigationResult.user_info.data.username === 'string'
                                      ? investigationResult.user_info.data.username
                                      : (typeof investigationResult.username === 'string'
                                          ? investigationResult.username
                                          : 'unknown'))))}
                          </p>
                          {((typeof investigationResult.user_info.data.description === 'string' && investigationResult.user_info.data.description) || 
                            (typeof investigationResult.user_info.data.bio === 'string' && investigationResult.user_info.data.bio)) && (
                            <p className="mt-2">
                              {typeof investigationResult.user_info.data.description === 'string' && investigationResult.user_info.data.description
                                ? investigationResult.user_info.data.description
                                : (typeof investigationResult.user_info.data.bio === 'string' ? investigationResult.user_info.data.bio : '')}
                            </p>
                          )}
                          {((typeof investigationResult.user_info.data.location === 'string' && investigationResult.user_info.data.location) || 
                            (typeof investigationResult.user_info.data.geoLocation === 'string' && investigationResult.user_info.data.geoLocation)) && (
                            <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                              <Globe className="h-4 w-4" />
                              {typeof investigationResult.user_info.data.location === 'string' && investigationResult.user_info.data.location
                                ? investigationResult.user_info.data.location
                                : (typeof investigationResult.user_info.data.geoLocation === 'string' ? investigationResult.user_info.data.geoLocation : '')}
                            </div>
                          )}
                          {(investigationResult.user_info.data.createdAt || 
                            investigationResult.user_info.data.created_at || 
                            investigationResult.user_info.data.joinDate) && (
                            <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              Joined {new Date(
                                investigationResult.user_info.data.createdAt || 
                                investigationResult.user_info.data.created_at || 
                                investigationResult.user_info.data.joinDate || 
                                new Date()
                              ).toLocaleDateString()}
                            </div>
                          )}
                          <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                            <span>{investigationResult.user_info.data.followers?.toLocaleString() || 
                                   investigationResult.user_info.data.followersCount?.toLocaleString() || 0} followers</span>
                            <span>{investigationResult.user_info.data.following?.toLocaleString() || 
                                   investigationResult.user_info.data.followingCount?.toLocaleString() || 0} following</span>
                            <span>{investigationResult.user_info.data.statusesCount?.toLocaleString() || 0} tweets</span>
                          </div>
                          {(investigationResult.user_info.data.url || 
                            investigationResult.user_info.data.website) && (
                            <a 
                              href={investigationResult.user_info.data.url || 
                                    investigationResult.user_info.data.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-sm text-blue-500 hover:underline"
                            >
                              View on X <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No user information available</p>
                  )}
                </CardContent>
              </Card>

              {/* Bot Detection */}
              {investigationResult.bot_detection && investigationResult.bot_detection.success && (
                <Card className={`border-2 ${
                  investigationResult.bot_detection.risk_level === 'high' ? 'border-red-500/60 bg-red-950/20' :
                  investigationResult.bot_detection.risk_level === 'medium' ? 'border-amber-500/60 bg-amber-950/20' :
                  'border-slate-500/40 bg-slate-950/20'
                }`}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span>🤖</span>
                      Bot Account Detection
                    </CardTitle>
                    <CardDescription>
                      {investigationResult.bot_detection.message}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-4">
                      <div className="rounded-lg border border-white/10 px-4 py-2">
                        <p className="text-xs text-muted-foreground">Suspicious</p>
                        <p className="text-xl font-bold">{investigationResult.bot_detection.suspicious_count ?? 0}</p>
                      </div>
                      <div className="rounded-lg border border-white/10 px-4 py-2">
                        <p className="text-xs text-muted-foreground">Sampled</p>
                        <p className="text-xl font-bold">{investigationResult.bot_detection.total_sampled ?? 0}</p>
                      </div>
                      <div className="rounded-lg border border-white/10 px-4 py-2">
                        <p className="text-xs text-muted-foreground">Risk Level</p>
                        <Badge variant={
                          investigationResult.bot_detection.risk_level === 'high' ? 'destructive' :
                          investigationResult.bot_detection.risk_level === 'medium' ? 'secondary' : 'outline'
                        }>
                          {investigationResult.bot_detection.risk_level ?? 'low'}
                        </Badge>
                      </div>
                    </div>
                    {investigationResult.bot_detection.suspicious_accounts && investigationResult.bot_detection.suspicious_accounts.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold mb-2">Potentially Bot-like Accounts</p>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {investigationResult.bot_detection.suspicious_accounts.map((acc, idx) => (
                            <div key={idx} className="flex items-center justify-between rounded border border-white/10 p-2 text-sm">
                              <div>
                                <a href={acc.link} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline font-medium">
                                  @{acc.username}
                                </a>
                                <span className="text-muted-foreground ml-2">
                                  {acc.followers} followers · {acc.following} following · {acc.tweets} tweets
                                </span>
                              </div>
                              <Badge variant="outline" className="text-amber-400 border-amber-500/50">
                                Score: {acc.bot_score}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Grok Enhanced Data */}
              {investigationResult.grok_enhanced && (
                <Card className="border-purple-500/50 bg-purple-950/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-purple-400">🤖</span>
                      xAI Grok Enhanced Profile Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {investigationResult.grok_enhanced.profile_data && (
                      <div className="space-y-3">
                        <div className="rounded-lg border border-purple-500/30 bg-black/50 p-4">
                          <p className="text-sm whitespace-pre-wrap text-white/90">
                            {investigationResult.grok_enhanced.profile_data}
                          </p>
                        </div>
                        {investigationResult.grok_enhanced.citations && investigationResult.grok_enhanced.citations.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            <p className="font-semibold mb-2">Sources:</p>
                            <div className="flex flex-wrap gap-2">
                              {investigationResult.grok_enhanced.citations.slice(0, 5).map((citation, idx) => (
                                <a
                                  key={idx}
                                  href={citation}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:underline truncate max-w-xs"
                                >
                                  {citation}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="followers" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Followers ({investigationResult.followers?.followers?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {followerList.length ? (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {followerList.map((follower, index) => (
                        <div key={index} className="border rounded-lg p-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                          {(follower.profilePicture || follower.avatar || follower.profile_image_url) ? (
                            <img 
                              src={follower.profilePicture || follower.avatar || follower.profile_image_url} 
                              alt={follower.userName || follower.username || follower.screenName}
                              className="w-12 h-12 rounded-full object-cover border border-green-500"
                              crossOrigin="anonymous"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                img.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center border border-green-500">
                              <User className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {follower.name || follower.displayName || follower.userName || follower.username || follower.screenName}
                              </span>
                              {(follower.isBlueVerified || follower.verified) && (
                                <Badge className="bg-blue-500 text-xs">Verified</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              @{follower.userName || follower.username || follower.screenName}
                            </p>
                            {(follower.description || follower.bio) && (
                              <p className="text-sm mt-1">{((follower.description || follower.bio) || '').substring(0, 100)}...</p>
                            )}
                            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                              <span>{(follower.followers || follower.followersCount || 0).toLocaleString()} followers</span>
                              <span>{(follower.following || follower.followingCount || 0).toLocaleString()} following</span>
                            </div>
                          </div>
                          {(follower.url || follower.website) && (
                            <a 
                              href={follower.url || follower.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">
                      {investigationResult.followers?.rate_limited
                        ? 'Partial follower data delivered (rate limited).'
                        : 'No followers data available'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="followings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Following ({investigationResult.followings?.followings?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {followingList.length ? (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {followingList.map((following, index) => {
                        // Handle both regular API and Grok API response formats
                        const profilePic = following.profilePicture || following.avatar || following.profile_image_url;
                        const displayName = following.name || following.displayName || following.userName || following.username || following.screenName;
                        const username = following.userName || following.username || following.screenName;
                        const followersCount = following.followers || following.followersCount || 0;
                        const followingCount = following.following || following.followingCount || 0;
                        const isVerified = following.isBlueVerified || following.verified;
                        const bio = following.description || following.bio;
                        const url = following.url || following.website || `https://x.com/${username}`;
                        
                        return (
                          <div key={index} className="border rounded-lg p-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                          {profilePic ? (
                            <img 
                              src={profilePic} 
                              alt={username}
                              className="w-12 h-12 rounded-full object-cover border border-green-500"
                              crossOrigin="anonymous"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                img.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center border border-green-500">
                              <User className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{displayName}</span>
                                {isVerified && (
                                  <Badge className="bg-blue-500 text-xs">Verified</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">@{username}</p>
                              {bio && (
                                <p className="text-sm mt-1">{bio.substring(0, 100)}{bio.length > 100 ? '...' : ''}</p>
                              )}
                              <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                                <span>{followersCount.toLocaleString()} followers</span>
                                <span>{followingCount.toLocaleString()} following</span>
                              </div>
                            </div>
                            <a 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">
                      {investigationResult.followings?.rate_limited
                        ? 'Partial following data delivered (rate limited).'
                        : 'No followings data available'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tweets" className="space-y-4">
              {/* Comprehensive Analytics Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Analytics Dashboard
                  </CardTitle>
                  <CardDescription>
                    Comprehensive analysis of {tweets.length} {queryType === 'topic_search' ? 'posts' : 'tweets'} with engagement metrics, activity timeline, and sentiment analysis.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Engagement Metrics Chart */}
                  {tweets.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Engagement Metrics Over Time
                      </p>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={tweets.map((t: any, idx: number) => {
                          const date = extractTweetTimestamp(t);
                          return {
                            index: idx + 1,
                            date: date ? new Date(date).toLocaleDateString() : `Post ${idx + 1}`,
                            likes: t.likeCount || t.likes || 0,
                            retweets: t.retweetCount || t.shares || 0,
                            replies: t.replyCount || t.comments || 0,
                            views: t.views || 0
                          };
                        }).slice(-20)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                          <YAxis stroke="#9ca3af" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                            labelStyle={{ color: '#f3f4f6' }}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="views" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                          <Area type="monotone" dataKey="likes" stackId="2" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} />
                          <Area type="monotone" dataKey="retweets" stackId="3" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                          <Area type="monotone" dataKey="replies" stackId="4" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Activity Timeline */}
                  {tweetAnalytics.hourlyDistribution.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Activity Timeline (24-Hour Distribution)</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={Array.from({ length: 24 }, (_, i) => {
                          const hourData = tweetAnalytics.hourlyDistribution.find(h => h.hour === i);
                          return {
                            hour: `${i.toString().padStart(2, '0')}:00`,
                            count: hourData?.count || 0
                          };
                        })}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="hour" stroke="#9ca3af" fontSize={10} angle={-45} textAnchor="end" height={60} />
                          <YAxis stroke="#9ca3af" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                            labelStyle={{ color: '#f3f4f6' }}
                          />
                          <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Word Cloud Visualization */}
                  {tweetAnalytics.topWords.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Word Cloud</p>
                      <div className="flex flex-wrap gap-2 p-4 bg-gray-900/50 rounded-lg border border-gray-700 min-h-[150px] items-center justify-center">
                        {tweetAnalytics.topWords.map(({ word, count }: { word: string; count: number }) => {
                          const maxCount = Math.max(...tweetAnalytics.topWords.map(w => w.count));
                          const size = Math.max(12, (count / maxCount) * 32);
                          const opacity = 0.6 + (count / maxCount) * 0.4;
                          return (
                            <span
                              key={word}
                              className="inline-block px-2 py-1 rounded"
                              style={{
                                fontSize: `${size}px`,
                                fontWeight: count > maxCount * 0.7 ? 'bold' : 'normal',
                                color: `rgba(16, 185, 129, ${opacity})`,
                                textShadow: `0 0 8px rgba(16, 185, 129, ${opacity * 0.5})`
                              }}
                            >
                              {word}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Sentiment Analysis */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Top Words</p>
                      <div className="flex flex-wrap gap-2">
                        {tweetAnalytics.topWords.length ? (
                          tweetAnalytics.topWords.map(({ word, count }: { word: string; count: number }) => (
                            <span
                              key={word}
                              className="rounded-full bg-green-500/20 px-3 py-1 text-xs font-medium text-green-300"
                            >
                              {word} ({count})
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">Not enough data</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Top Hashtags</p>
                      <div className="flex flex-wrap gap-2">
                        {tweetAnalytics.topHashtags.length ? (
                          tweetAnalytics.topHashtags.map(({ tag, count }: { tag: string; count: number }) => (
                            <span
                              key={tag}
                              className="rounded-full border border-green-600/40 px-3 py-1 text-xs font-medium text-white/90"
                            >
                              #{tag} ({count})
                            </span>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground">No hashtags yet.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Top Mentions */}
                  {tweetAnalytics.topMentions.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Top Mentions</p>
                      <div className="flex flex-wrap gap-2">
                        {tweetAnalytics.topMentions.map(({ handle, count }: { handle: string; count: number }) => (
                          <span
                            key={handle}
                            className="rounded-full border border-blue-500/40 px-3 py-1 text-xs font-medium text-white/90"
                          >
                            @{handle} ({count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sentiment Analysis */}
                  {(investigationResult as any).search_results?.sentiment_analysis && (
                    <div className="space-y-4 border-t border-gray-700 pt-4">
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Sentiment Analysis
                      </p>
                      
                      {(() => {
                        const sentimentData = (investigationResult as any).search_results?.sentiment_analysis;
                        const overall = sentimentData?.overall;
                        if (!overall) return null;
                        
                        const positive = overall.positive || 0;
                        const negative = overall.negative || 0;
                        const neutral = overall.neutral || 0;
                        
                        return (
                          <div className="grid gap-4 md:grid-cols-2">
                            {/* Sentiment Distribution Chart */}
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground">Overall Sentiment Distribution</p>
                              <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={[
                                  { name: 'Positive', value: positive, color: '#10b981' },
                                  { name: 'Negative', value: negative, color: '#ef4444' },
                                  { name: 'Neutral', value: neutral, color: '#6b7280' }
                                ]}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                                  <YAxis stroke="#9ca3af" fontSize={12} />
                                  <Tooltip 
                                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                                    labelStyle={{ color: '#f3f4f6' }}
                                  />
                                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                    {[
                                      { name: 'Positive', value: positive, color: '#10b981' },
                                      { name: 'Negative', value: negative, color: '#ef4444' },
                                      { name: 'Neutral', value: neutral, color: '#6b7280' }
                                    ].map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Sentiment Stats */}
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground">Sentiment Breakdown</p>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between p-2 rounded bg-green-500/10 border border-green-500/20">
                                  <span className="text-sm text-green-400">Positive</span>
                                  <span className="text-sm font-bold text-green-300">
                                    {positive.toFixed(1)}%
                                  </span>
                                </div>
                                <div className="flex items-center justify-between p-2 rounded bg-red-500/10 border border-red-500/20">
                                  <span className="text-sm text-red-400">Negative</span>
                                  <span className="text-sm font-bold text-red-300">
                                    {negative.toFixed(1)}%
                                  </span>
                                </div>
                                <div className="flex items-center justify-between p-2 rounded bg-gray-500/10 border border-gray-500/20">
                                  <span className="text-sm text-gray-400">Neutral</span>
                                  <span className="text-sm font-bold text-gray-300">
                                    {neutral.toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Key Themes */}
                      {(investigationResult as any).search_results?.sentiment_analysis?.themes && 
                       (investigationResult as any).search_results.sentiment_analysis.themes.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">Key Emotional Themes</p>
                          <div className="flex flex-wrap gap-2">
                            {(investigationResult as any).search_results.sentiment_analysis.themes.map((theme: string, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/50">
                                {theme}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-1">
                    <label className="text-sm font-semibold">AI Question / Focus (optional)</label>
                    <textarea
                      value={aiFocus}
                      onChange={(e) => setAiFocus(e.target.value)}
                      placeholder="Ask a custom question about the selected posts (e.g., 'Fact check these posts', 'Analyze sentiment', 'Find contradictions', 'Summarize key points', etc.)"
                      className="w-full rounded border border-gray-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-gray-500"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {selectedTweetIds.length > 0 
                        ? `${selectedTweetIds.length} post${selectedTweetIds.length > 1 ? 's' : ''} selected. ${aiFocus ? 'Will analyze with your custom question.' : 'Will generate a profile report.'}`
                        : 'Select posts above to analyze them with AI.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      className="text-xs uppercase border border-gray-800 bg-transparent px-3 py-1"
                      onClick={handleSelectAllTweets}
                      disabled={!tweets.length}
                    >
                      Select all ({tweets.length})
                    </Button>
                    <Button
                      className="text-xs uppercase border border-gray-800 bg-transparent px-3 py-1"
                      onClick={handleClearSelection}
                      disabled={!selectedTweetIds.length}
                    >
                      Clear selection
                    </Button>
                    <Button
                      className="text-xs uppercase px-3 py-1"
                      onClick={handleSendTweetsToAI}
                      disabled={!selectedTweetIds.length || isAiAnalyzing}
                    >
                      {isAiAnalyzing ? 'Analyzing...' : `Send ${selectedTweetIds.length || 'selected'} to AI${aiFocus ? ' (with custom question)' : ''}`}
                    </Button>
                  </div>
                  {aiError && (
                    <p className="text-sm text-red-400">{aiError}</p>
                  )}
                  {aiAnalysis && (
                    <div className="space-y-2 rounded border border-green-500/60 bg-black/50 p-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Profile insights</span>
                        {aiTimestamp && <span>{new Date(aiTimestamp).toLocaleString()}</span>}
                      </div>
                      <div className="text-sm text-gray-300 leading-relaxed
                        [&_h1]:text-green-300 [&_h1]:font-mono [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1
                        [&_h2]:text-green-300 [&_h2]:font-mono [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1
                        [&_h3]:text-green-400 [&_h3]:font-mono [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-0.5
                        [&_strong]:text-white [&_strong]:font-semibold
                        [&_p]:my-1 [&_p]:leading-relaxed
                        [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-1 [&_ul_li]:my-0.5
                        [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:my-1 [&_ol_li]:my-0.5
                        [&_code]:text-green-400 [&_code]:bg-black/40 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
                        <ReactMarkdown>{aiAnalysis}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{queryType === 'topic_search' ? 'Posts' : 'Tweets'} ({tweets.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {investigationResult.tweets?.success && tweets.length ? (
                    <div className="space-y-4 max-h-[800px] overflow-y-auto">
                      {tweets.map((tweet, index) => {
                        const tweetId = extractTweetId(tweet, index);
                        const isSelected = selectedTweetIds.includes(tweetId);
                        const createdAtRaw = extractTweetTimestamp(tweet);
                        const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null;
                        const canShowDate = createdAtDate && !Number.isNaN(createdAtDate.getTime());

                        // Safely derive author username and name from various possible shapes
                        const rawUsername =
                          (typeof tweet.user?.userName === 'string' && tweet.user.userName) ||
                          (typeof tweet.user?.username === 'string' && tweet.user.username) ||
                          (typeof tweet.user?.screenName === 'string' && tweet.user.screenName) ||
                          (typeof tweet.username === 'string' && tweet.username) ||
                          (typeof tweet.handle === 'string' && tweet.handle) ||
                          (typeof tweet.author === 'string' && tweet.author) ||
                          (typeof tweet.author?.username === 'string' && tweet.author.username) ||
                          (typeof tweet.author?.userName === 'string' && tweet.author.userName) ||
                          null;

                        const authorUsername = rawUsername || 'unknown';

                        const rawAuthorName =
                          (typeof tweet.user?.name === 'string' && tweet.user.name) ||
                          (typeof tweet.user?.displayName === 'string' && tweet.user.displayName) ||
                          (typeof tweet.author?.name === 'string' && tweet.author.name) ||
                          null;

                        const authorName = rawAuthorName || authorUsername;
                        // Get profile picture - try multiple sources and handle CORS
                        const profilePic = tweet.user?.profilePicture || tweet.user?.avatar || tweet.user?.profile_image_url || 
                                         tweet.metadata?.author_profile_picture || tweet.metadata?.author_profile_picture_url || null;
                        const tweetText = extractTweetText(tweet);
                        const mediaUrls = getMediaUrlsFromPost(tweet);
                        
                        return (
                          <div
                            key={tweetId}
                            className={`border rounded-lg p-4 transition-colors ${isSelected ? 'border-green-500 bg-white/5' : 'border-gray-700 bg-transparent hover:border-gray-600'}`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleTweetSelection(tweetId)}
                                className="mt-1 h-4 w-4 accent-green-400 cursor-pointer"
                              />
                              <div className="flex-1 space-y-3">
                                <div className="flex items-start gap-3">
                                  {profilePic ? (
                                    <img
                                      src={profilePic}
                                      alt={authorUsername}
                                      className="w-12 h-12 rounded-full object-cover border border-gray-600 flex-shrink-0"
                                      crossOrigin="anonymous"
                                      referrerPolicy="no-referrer"
                                      onError={(e) => {
                                        const img = e.target as HTMLImageElement;
                                        // Try to fix CORS issues
                                        if (profilePic && (profilePic.includes('pbs.twimg.com') || profilePic.includes('twimg.com'))) {
                                          // Twitter images - try direct URL
                                          img.src = profilePic;
                                        } else {
                                          img.style.display = 'none';
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center border border-gray-600 flex-shrink-0">
                                      <User className="w-6 h-6 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="flex-1 space-y-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-semibold text-white">{authorName}</span>
                                      <span className="text-xs text-muted-foreground">
                                        @{authorUsername}
                                      </span>
                                      {tweet.user?.verified && (
                                        <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500 px-1.5 py-0">
                                          ✓ Verified
                                        </Badge>
                                      )}
                                      {canShowDate && (
                                        <span className="text-xs text-muted-foreground">
                                          · {createdAtDate.toLocaleString()}
                                        </span>
                                      )}
                                    </div>
                                    {typeof tweet.user?.bio === 'string' && tweet.user.bio && (
                                      <p className="text-xs text-muted-foreground line-clamp-2">{tweet.user.bio}</p>
                                    )}
                                    <p className="text-sm whitespace-pre-wrap text-white leading-relaxed break-words">{tweetText}</p>
                                  </div>
                                </div>
                                {/* Media Display */}
                                <PostMediaGrid mediaUrls={mediaUrls} />
                                
                                {/* Hashtags and Mentions */}
                                {(tweet.hashtags?.length > 0 || tweet.mentions?.length > 0) && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {tweet.hashtags?.map((tag: string, idx: number) => (
                                      <Badge key={idx} variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/50">
                                        #{tag}
                                      </Badge>
                                    ))}
                                    {tweet.mentions?.map((mention: string, idx: number) => (
                                      <Badge key={idx} variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/50">
                                        @{mention}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Engagement Metrics */}
                                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-gray-700">
                                  {tweet.views !== undefined && tweet.views > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Eye className="w-3 h-3" />
                                      {tweet.views.toLocaleString()} views
                                    </span>
                                  )}
                                  {tweet.retweetCount !== undefined && (
                                    <span className="flex items-center gap-1">
                                      🔄 {tweet.retweetCount?.toLocaleString() || 0}
                                    </span>
                                  )}
                                  {tweet.likeCount !== undefined && (
                                    <span className="flex items-center gap-1">
                                      ❤️ {tweet.likeCount?.toLocaleString() || 0}
                                    </span>
                                  )}
                                  {tweet.replyCount !== undefined && (
                                    <span className="flex items-center gap-1">
                                      💬 {tweet.replyCount?.toLocaleString() || 0}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-700">
                                  {tweet.url && (
                                    <a
                                      href={tweet.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 hover:underline"
                                    >
                                      View on X <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs h-7 px-2 text-amber-400 hover:text-amber-300"
                                    onClick={() => {
                                      setSelectedTweetIds([extractTweetId(tweet, index)]);
                                      setAiFocus('Fact check this post. Identify any claims that may be misleading or false. Provide sources if possible.');
                                      setActiveTab('tweets');
                                    }}
                                  >
                                    Fact Check
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs h-7 px-2 text-violet-400 hover:text-violet-300"
                                    onClick={() => {
                                      setSelectedTweetIds([extractTweetId(tweet, index)]);
                                      setAiFocus('Analyze this post in detail: sentiment, tone, key themes, potential bias, and engagement patterns.');
                                      setActiveTab('tweets');
                                    }}
                                  >
                                    Analyze Post
                                  </Button>
                                  {authorUsername !== 'unknown' && authorUsername !== 'search' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-xs h-7 px-2"
                                      onClick={async () => {
                                        setUsername(authorUsername);
                                        setFetchingProfile(authorUsername);
                                        await handleInvestigation();
                                        setFetchingProfile(null);
                                      }}
                                      disabled={fetchingProfile === authorUsername}
                                    >
                                      {fetchingProfile === authorUsername ? (
                                        <>
                                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                          Loading...
                                        </>
                                      ) : (
                                        <>
                                          <User className="w-3 h-3 mr-1" />
                                          View Profile
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : investigationResult.tweets?.rate_limited ? (
                    <p className="text-muted-foreground text-yellow-500">
                      Rate limited. Tweets will be fetched on next request.
                    </p>
                  ) : (
                    <p className="text-muted-foreground">No tweets data available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="link-analysis" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Tweet Link Analysis
                  </CardTitle>
                  <CardDescription>
                    Interactive graph showing connections between tweets, mentions, hashtags, and engagement. Click nodes to explore.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {tweets.length > 0 ? (
                    <XTweetLinkAnalysisGraph
                      tweets={tweets}
                      centralUsername={investigationResult.username}
                      height={500}
                    />
                  ) : (
                    <p className="text-muted-foreground py-8 text-center">
                      No tweets available for link analysis. Run an investigation first.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="network" className="space-y-4">
              {/* Grok Relationship Analysis */}
              {investigationResult.grok_relationship_analysis && investigationResult.grok_relationship_analysis.success && (
                <Card className="border-pink-500/50 bg-pink-950/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-pink-400">🔗</span>
                      xAI Grok Relationship Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {investigationResult.grok_relationship_analysis.analysis && (
                      <div className="rounded-lg border border-pink-500/30 bg-black/50 p-4">
                        <p className="text-sm whitespace-pre-wrap text-white/90">
                          {investigationResult.grok_relationship_analysis.analysis}
                        </p>
                      </div>
                    )}

                    {investigationResult.grok_relationship_analysis.insights && (
                      <div className="space-y-4">
                        {investigationResult.grok_relationship_analysis.insights.mutual_connections && 
                         investigationResult.grok_relationship_analysis.insights.mutual_connections.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold mb-2">Mutual Connections:</p>
                            <div className="flex flex-wrap gap-2">
                              {investigationResult.grok_relationship_analysis.insights.mutual_connections.map((username, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  @{username}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {investigationResult.grok_relationship_analysis.insights.influencer_relationships && 
                         investigationResult.grok_relationship_analysis.insights.influencer_relationships.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold mb-2">Influencer Relationships:</p>
                            <div className="flex flex-wrap gap-2">
                              {investigationResult.grok_relationship_analysis.insights.influencer_relationships.map((username, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs bg-purple-950/50">
                                  @{username}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {investigationResult.grok_relationship_analysis.insights.network_influence_score !== undefined && (
                          <div className="rounded-lg border border-pink-500/30 bg-black/50 p-4">
                            <p className="text-sm font-semibold mb-2">Network Influence Score:</p>
                            <p className="text-3xl font-bold text-pink-400">
                              {investigationResult.grok_relationship_analysis.insights.network_influence_score.toFixed(1)}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardHeader>
                  <CardTitle>Network Graph</CardTitle>
                  <CardDescription>
                    Visual representation of the user's network connections
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {investigationResult.user_info?.data && typeof investigationResult.user_info.data === 'object' && investigationResult.user_info.data !== null ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">Filters:</span>
                          <button
                            type="button"
                            className={`rounded px-3 py-1 text-xs font-semibold uppercase transition ${
                              showFollowersInGraph
                                ? 'border border-green-400/80 bg-green-500/80 text-black'
                                : 'border border-white/30 bg-transparent text-white/60'
                            }`}
                            onClick={() => setShowFollowersInGraph((prev) => !prev)}
                          >
                            Followers
                          </button>
                          <button
                            type="button"
                            className={`rounded px-3 py-1 text-xs font-semibold uppercase transition ${
                              showFollowingsInGraph
                                ? 'border border-green-400/80 bg-green-500/80 text-black'
                                : 'border border-white/30 bg-transparent text-white/60'
                            }`}
                            onClick={() => setShowFollowingsInGraph((prev) => !prev)}
                          >
                            Following
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">Sort:</span>
                          <select
                            value={graphSortBy}
                            onChange={(e) => setGraphSortBy(e.target.value as 'default' | 'followers' | 'following')}
                            className="rounded border border-white/20 bg-transparent px-2 py-1 text-xs text-white"
                          >
                            {GRAPH_SORT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="border rounded-lg border-green-500/40 bg-gray-50/10 dark:bg-gray-900/40 dark:border-green-500 min-h-[600px] p-2">
                        <TwitterNetworkGraph
                          userInfo={investigationResult.user_info}
                          followers={filteredGraphFollowers}
                          followings={filteredGraphFollowings}
                          onNodeClick={(node) => {
                            setSelectedGraphNode(node);
                          }}
                          height={600}
                        />
                        <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {GRAPH_LEGEND_ENTRIES.map((entry) => (
                            <div key={entry.label} className="flex items-center gap-2">
                              <span
                                className="h-3 w-3 rounded-full border border-white/60"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span>{entry.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-lg border border-green-500/40 bg-black/40 p-4 text-sm text-white">
                        {selectedGraphNode ? (
                          <div className="space-y-2">
                            <div className="flex items-start gap-3">
                              {selectedGraphNode.avatar ? (
                                <img
                                  src={selectedGraphNode.avatar}
                                  alt={selectedGraphNode.username || selectedGraphNode.label}
                                  className="h-12 w-12 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-xs font-semibold text-white/70">
                                  {selectedGraphNode.label?.charAt(0) || 'U'}
                                </div>
                              )}
                              <div>
                                <p className="text-lg font-semibold leading-tight text-white">
                                  {selectedGraphNode.label}
                                  {selectedGraphNode.verified && (
                                    <span className="ml-1 text-xs uppercase text-blue-300">Verified</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  @{selectedGraphNode.username || 'unknown'}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs font-semibold text-muted-foreground">
                              <span>
                                {selectedGraphNode.followers?.toLocaleString() || 0} followers
                              </span>
                              <span>
                                {selectedGraphNode.following?.toLocaleString() || 0} following
                              </span>
                              <span className="capitalize">{selectedGraphNode.type}</span>
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs text-white/80">
                              {selectedGraphNode.description && (
                                <p className="text-xs text-white/80">{selectedGraphNode.description}</p>
                              )}
                              {selectedGraphNode.username && (
                                <a
                                  href={`https://x.com/${selectedGraphNode.username}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-green-400 underline-offset-2 hover:underline"
                                >
                                  View profile on X
                                </a>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Click a node in the graph to see detailed metadata here.
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No network data available for visualization</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Loading State */}
      {isInvestigating && (
        <Card>
          <CardContent className="text-center py-12">
            <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-medium mb-2">Investigating X Account...</h3>
            <p className="text-muted-foreground">
              Fetching user information, followers, and followings
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!investigationResult && !isInvestigating && (
        <Card>
          <CardContent className="text-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Start Your X Investigation</h3>
            <p className="text-muted-foreground mb-4">
              Enter a Twitter/X username to begin comprehensive investigation
            </p>
          </CardContent>
        </Card>
      )}

      </>}
    </div>
  );
}

